import React, { useState, useEffect, useCallback } from "react";
import { 
  Box, 
  Card, 
  Grid, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  TableContainer, 
  Paper,
  Chip,
  Stack,
  styled,
  useTheme
} from "@mui/material";
import { fetchHistoricalSyncSummary } from "../../services/historicalDataSyncService";
import { historicalDataOverAllSyncSummary } from "../../types/types";
import { Loader } from "../../components/loader/Loader";
import { 
  Storage as StorageIcon, 
  Sync as SyncIcon, 
  Cloud as CloudIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { Tile } from "./tile";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${TableHead}`]: {
    backgroundColor: '#f0fbff',
    color: '#227c9d',
    fontWeight: 600,
    fontSize: '14px',
    padding: theme.spacing(2),
  },
  [`&.${TableBody}`]: {
    fontSize: 14,
    padding: theme.spacing(1.5),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    boxShadow: theme.shadows[2],
    transition: 'all 0.3s ease',
  },
}));

const StatusBadge = ({ status }: { status: string }) => {
  const statusLower = status.toLowerCase();
  const theme = useTheme();
  
  return (
    <Chip
      label={status}
      size="small"
      icon={statusLower === 'completed' ? 
        <CheckCircleIcon /> : 
        statusLower === 'failed' ? 
        <ErrorIcon /> : 
        <WarningIcon />
      }
      color={
        statusLower === 'completed' ? 'success' : 
        statusLower === 'failed' ? 'error' : 
        'warning'
      }
      sx={{ 
        fontWeight: 600,
        px: 1,
        minWidth: 100,
        '& .MuiChip-icon': {
          color: statusLower === 'completed' ? 
            theme.palette.success.light : 
            statusLower === 'failed' ? 
            theme.palette.error.light : 
            theme.palette.warning.light
        }
      }}
    />
  );
};

const DifferenceBadge = ({ value }: { value: number }) => {
  return (
    <Chip
      label={value}
      size="small"
      color={value === 0 ? 'success' : 'error'}
      sx={{ 
        fontWeight: 600,
        px: 1,
        minWidth: 60
      }}
    />
  );
};

const OverallTableWiseTab = () => {
  const theme = useTheme();
  const authContext = JSON.parse(localStorage.authContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [jobSummary, setJobSummary] = useState<historicalDataOverAllSyncSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: historicalDataOverAllSyncSummary = await fetchHistoricalSyncSummary(authContext.env, authContext.user.lob);
      console.log(JSON.stringify(response));
      if (response) {
        setJobSummary(response);
      } else {
        setError("No data received from server");
      }
    } catch (error) {
      console.error("Failed to fetch historical sync summary:", error);
      setError("Failed to fetch data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [authContext.env, authContext.user.lob]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  if (!jobSummary) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">No historical sync data available</Typography>
      </Box>
    );
  }

  const hasOverallStatus = jobSummary.overAllStatus?.length > 0;
  const hasTableWiseData = jobSummary.tableWise?.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={4}>
        {/* Overall Status Section */}
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
            Overall Status
          </Typography>

          {hasOverallStatus ? (
            <Stack spacing={3}>
              {jobSummary.overAllStatus.map((status, index) => (
                <Card 
                  key={index} 
                  elevation={3}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    '&:hover': {
                      boxShadow: theme.shadows[6],
                    }
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Group: {status.group_Id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Date: {status.taskDate}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Tile 
                        title="MySQL Total" 
                        value={status.mysqlTotal} 
                        icon={<StorageIcon color="primary" />} 
                       
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Tile 
                        title="Redshift Total" 
                        value={status.redshiftTotal} 
                        icon={<StorageIcon color="secondary" />} 
                        
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Tile 
                        title="S3 Total" 
                        value={status.s3Total} 
                        icon={<CloudIcon color="info" />} 
                        
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Tile 
                        title="MySQL Difference" 
                        value={status.mysqlDifference} 
                        icon={<SyncIcon color={status.mysqlDifference === 0 ? "success" : "error"} />} 
                        highlight={status.mysqlDifference !== 0}
                        
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Tile 
                        title="S3 Difference" 
                        value={status.s3Difference} 
                        icon={<SyncIcon color={status.s3Difference === 0 ? "success" : "error"} />} 
                        highlight={status.s3Difference !== 0}
                        
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Stack>
          ) : (
            <Card 
              elevation={4} 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                borderRadius: 3,
                backgroundColor: theme.palette.grey[50]
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No Overall Status Data Available
              </Typography>
            </Card>
          )}
        </Box>

        {/* Table Wise Summary Section */}
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
            Date Wise Summary
          </Typography>

          {hasTableWiseData ? (
            <Stack spacing={3}>
              {jobSummary.tableWise.map((group, index) => {
                try {
                  const groupItems = JSON.parse(group.groupItems);
                  return (
                    <Card 
                      key={index} 
                      elevation={3}
                      sx={{ 
                        p: 3, 
                        borderRadius: 3,
                        '&:hover': {
                          boxShadow: theme.shadows[6],
                        }
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2
                      }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Group: {group.group_Id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Date: {group.taskDate}
                        </Typography>
                      </Box>

                      <TableContainer 
                        component={Paper} 
                        sx={{ 
                          borderRadius: 2, 
                          maxHeight: 400, 
                          overflow: 'auto',
                          boxShadow: theme.shadows[2]
                        }}
                      >
                        <Table stickyHeader>
                        <TableHead>
                                <TableRow>
                                  {[
                                    "Table Name", "MySQL Records", "S3 Records", "Redshift Records",
                                    "MySQL Difference", "S3 Difference", "Retention Period", 
                                    "Start Date", "End Date", "Status"
                                  ].map((header) => (
                                    <TableCell
                                      key={header}
                                      sx={{ 
                                        backgroundColor: '#f0fbff',
                                        color: '#227c9d',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        padding: theme.spacing(2)
                                      }}
                                    >
                                      {header}
                                    </TableCell>
                                  ))}
                                </TableRow>
                            </TableHead>

                          <TableBody>
                            {groupItems.map((item: any, idx: number) => (
                              <StyledTableRow key={idx}>
                                <TableCell>{item.table_name}</TableCell>
                                <TableCell>{item.totalexternalrecords}</TableCell>
                                <TableCell>{item.totals3records}</TableCell>
                                <TableCell>{item.totalredshiftrecords}</TableCell>
                                <TableCell>
                                  <DifferenceBadge value={item.mysqldifference} />
                                </TableCell>
                                <TableCell>
                                  <DifferenceBadge value={item.s3difference} />
                                </TableCell>
                                <TableCell>{item.retentionperiod}</TableCell>
                                <TableCell>{item.startdate}</TableCell>
                                <TableCell>{item.enddate}</TableCell>
                                <TableCell>
                                  <StatusBadge status={item.status} />
                                </TableCell>
                              </StyledTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  );
                } catch (e) {
                  console.error("Error parsing group items:", e);
                  return null;
                }
              })}
            </Stack>
          ) : (
            <Card 
              elevation={4} 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                borderRadius: 3,
                backgroundColor: theme.palette.grey[50]
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No Date Wise Summary Available
              </Typography>
            </Card>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default OverallTableWiseTab;